﻿// Світлові літери: кожна літера — порожній корпус (стінки + задня стінка) і знімна кришка-розсіювач з бортиком.
// Отвори під провід ставить користувач: подвійний дотик по бічній стінці або по задній, мітку можна тягнути, подвійний дотик по мітці — прибрати.
// Перегляд: зібрано / розібрано / на стіні вночі (кришки світяться, ореол на стіні), літери в рядок або стовпчиком.
// Файли: STL (усе разом), ZIP (кожна деталь окремо). Параметри зберігаються в браузері через ctx.store, якщо сторінка його дає.
import { bedPicker, bedText, createStage, fitsBed, glyphShapes, loadFont, mergeGeometries, scope, solid, THREE, toGeometry, type Ctx, type Model, type Part } from '@gen3d/core';
import type { CrossSection, Manifold } from 'manifold-3d/manifold';

type Pt = [number, number];
type View = 'assembled' | 'exploded' | 'wall';
type Layout = 'h' | 'v';
/** Отвір під провід: літера і точка в частках висоти літери від її початку (так переживає зміну розміру); back — у задній стінці. */
type Hole = { li: number; u: number; v: number; back?: boolean };
/** Сховище сторінки (localStorage під генератор). Поки його немає в типах ядра — описано тут. */
type Store = { load(): unknown; save(v: unknown): void };
type S = {
  text: string; font: string; H: number; D: number;
  W: number; B: number; L: number; SW: number; SD: number; C: number;
  HW: number;
  mount: 'none' | 'screws'; HS: number;
  light: string; layout: Layout;
};
type NumKey = 'H' | 'D' | 'W' | 'B' | 'L' | 'SW' | 'SD' | 'C' | 'HW' | 'HS';

// Тільки щільні шрифти: у тонкий штрих не влізе стрічка. Усі з українськими літерами (перевірено для таблички).
const FONTS: [id: string, name: string][] = [
  ['russo-one-400', 'Russo One'], ['rubik-700', 'Rubik'], ['unbounded-700', 'Unbounded'], ['montserrat-700', 'Montserrat'],
  ['manrope-800', 'Manrope'], ['rubik-mono-one-400', 'Rubik Mono One'], ['exo-2-700', 'Exo 2'], ['oswald-600', 'Oswald'],
  ['nunito-800', 'Nunito'], ['ubuntu-700', 'Ubuntu'], ['roboto-slab-700', 'Roboto Slab'], ['press-start-2p-400', 'Press Start 2P'],
];
const COLORS = ['#FFD9A0', '#EAF4FF', '#FF3B30', '#FF8A00', '#FFD60A', '#34C759', '#32ADE6', '#2F5BFF', '#FF4FA3'];


/**
 * Текст без символов, которых нет в шрифте (эмодзи, ★): вместо них шрифт рисует прямоугольник-заглушку,
 * и он становился буквой. Пробельные — пробелом, невидимые служебные (селектор варианта у эмодзи, ZWJ) — молча.
 */
function inFont(font: Awaited<ReturnType<typeof loadFont>>, text: string, miss: Set<string>) {
  let out = '';
  for (const ch of text.normalize('NFC')) {
    if (/\s/.test(ch)) out += ' ';
    else if (font.charToGlyphIndex(ch) > 0) out += ch;
    else if (!/[\p{M}\p{Cf}]/u.test(ch)) miss.add(ch);
  }
  return out;
}
const T = {
  uk: {
    text: 'Напис', font: 'Шрифт', H: 'Висота літер', D: 'Глибина корпусу',
    hintDepth: 'Чим глибший корпус, тим рівніше світло: при малій глибині світлодіоди видно крізь кришку окремими точками.',
    fit: 'Стінки і кришка', W: 'Стінка', B: 'Задня стінка', L: 'Кришка', SW: 'Товщина бортика', SD: 'Глибина бортика', C: 'Зазор на сторону',
    hintFit: 'Бортик кришки входить у корпус. Зазор залежить від принтера і пластику — спершу надрукуйте пробники.',
    wire: 'Отвори під провід', HW: 'Діаметр отвору під провід',
    holes: (n: number) => n ? `Отворів: ${n}.` : 'Отворів поки немає.',
    hintHoles: 'Двічі торкніться бічної стінки літери — отвір буде в ній біля задньої частини; двічі торкніться дна або задньої стінки — наскрізний отвір у задній стінці. Синю мітку можна перетягнути, подвійний дотик по ній прибирає отвір.',
    clear: 'Прибрати всі',
    mount: 'Кріплення', none: 'Без отворів', screws: 'Шурупи', HS: 'Діаметр отвору',
    hintMount: 'Два отвори в задній стінці, у найширшому місці літери.',
    light: 'Колір світла', lightHint: 'Світло видно у вигляді «На стіні». Кришки друкуйте з білого пластику лицем донизу, із заповненням 100%.',
    views: { assembled: 'Зібрано', exploded: 'Розібрано', wall: 'На стіні' } as Record<View, string>,
    layouts: { h: 'Горизонтально', v: 'Вертикально' } as Record<Layout, string>,
    letters: (n: number) => `${n} ${n % 10 === 1 && n % 100 !== 11 ? 'літера' : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100) ? 'літери' : 'літер'}`,
    mm: 'мм', min: 'мін.', max: 'макс.',
    ok: 'Модель готова', empty: 'Введіть напис', noGlyph: 'Немає в шрифті — пропущено: {c}',
    bedBad: (l: string[], b: string) => `${l.length > 1 ? 'Літери більші' : 'Літера більша'} за стіл ${b} мм: ${l.join(' ')}`,
    mountBad: (l: string) => `Немає місця під кріплення: ${l}`,
    loading: 'Будую модель…', fontErr: 'Не вдалося завантажити шрифт',
    def: 'КАВА',
  },
  ru: {
    text: 'Надпись', font: 'Шрифт', H: 'Высота букв', D: 'Глубина корпуса',
    hintDepth: 'Чем глубже корпус, тем ровнее свет: при малой глубине светодиоды видны сквозь крышку отдельными точками.',
    fit: 'Стенки и крышка', W: 'Стенка', B: 'Задняя стенка', L: 'Крышка', SW: 'Толщина бортика', SD: 'Глубина бортика', C: 'Зазор на сторону',
    hintFit: 'Бортик крышки входит в корпус. Зазор зависит от принтера и пластика — сначала напечатайте пробники.',
    wire: 'Отверстия под провод', HW: 'Диаметр отверстия под провод',
    holes: (n: number) => n ? `Отверстий: ${n}.` : 'Отверстий пока нет.',
    hintHoles: 'Дважды коснитесь боковой стенки буквы — отверстие будет в ней у задней части; дважды коснитесь дна или задней стенки — сквозное отверстие в задней стенке. Синюю метку можно перетащить, двойное касание по ней убирает отверстие.',
    clear: 'Убрать все',
    mount: 'Крепление', none: 'Без отверстий', screws: 'Шурупы', HS: 'Диаметр отверстия',
    hintMount: 'Два отверстия в задней стенке, в самом широком месте буквы.',
    light: 'Цвет света', lightHint: 'Свет виден в виде «На стене». Крышки печатайте из белого пластика лицом вниз, с заполнением 100%.',
    views: { assembled: 'Собрано', exploded: 'Разобрано', wall: 'На стене' } as Record<View, string>,
    layouts: { h: 'Горизонтально', v: 'Вертикально' } as Record<Layout, string>,
    letters: (n: number) => `${n} ${n % 10 === 1 && n % 100 !== 11 ? 'буква' : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100) ? 'буквы' : 'букв'}`,
    mm: 'мм', min: 'мин.', max: 'макс.',
    ok: 'Модель готова', empty: 'Введите надпись', noGlyph: 'Нет в шрифте — пропущено: {c}',
    bedBad: (l: string[], b: string) => `${l.length > 1 ? 'Буквы больше' : 'Буква больше'} стола ${b} мм: ${l.join(' ')}`,
    mountBad: (l: string) => `Нет места под крепление: ${l}`,
    loading: 'Строю модель…', fontErr: 'Не удалось загрузить шрифт',
    def: 'КОФЕ',
  },
  en: {
    text: 'Text', font: 'Font', H: 'Letter height', D: 'Body depth',
    hintDepth: 'A deeper body gives more even light: in a shallow one the LEDs show through the lid as separate dots.',
    fit: 'Walls and lid', W: 'Wall', B: 'Back', L: 'Lid', SW: 'Lip thickness', SD: 'Lip depth', C: 'Clearance per side',
    hintFit: 'The lid lip slides into the body. Clearance depends on the printer and filament — print the test pieces first.',
    wire: 'Wire holes', HW: 'Wire hole diameter',
    holes: (n: number) => n ? `Holes: ${n}.` : 'No holes yet.',
    hintHoles: 'Double-tap a side wall to put a hole in it near the back; double-tap the floor or the back to put a hole through the back. Drag the blue marker to move it, double-tap it to remove.',
    clear: 'Remove all',
    mount: 'Mounting', none: 'No holes', screws: 'Screws', HS: 'Hole diameter',
    hintMount: 'Two holes in the back, in the widest part of the letter.',
    light: 'Light color', lightHint: 'The light shows in the “On the wall” view. Print lids in white filament face down at 100% infill.',
    views: { assembled: 'Assembled', exploded: 'Exploded', wall: 'On the wall' } as Record<View, string>,
    layouts: { h: 'Horizontal', v: 'Vertical' } as Record<Layout, string>,
    letters: (n: number) => `${n} letter${n === 1 ? '' : 's'}`,
    mm: 'mm', min: 'min', max: 'max',
    ok: 'Model ready', empty: 'Type some text', noGlyph: 'Not in the font, skipped: {c}',
    bedBad: (l: string[], b: string) => `${l.length > 1 ? 'Letters are' : 'Letter is'} larger than the ${b} mm bed: ${l.join(' ')}`,
    mountBad: (l: string) => `No room for mounting holes: ${l}`,
    loading: 'Building model…', fontErr: 'Could not load the font',
    def: 'OPEN',
  },
};

// [мін, макс, крок]
const LIM: Record<NumKey, [number, number, number]> = {
  H: [40, 240, 1], D: [10, 80, 1],
  W: [0.8, 5, 0.1], B: [0.8, 4, 0.1], L: [0.6, 3, 0.1], SW: [0.6, 2, 0.1], SD: [2, 10, 0.5], C: [0.05, 0.5, 0.05],
  HW: [3, 10, 0.5], HS: [3, 6, 0.5],
};

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const clamp = (v: number, [a, b]: [number, number, number]) => Math.min(b, Math.max(a, v));
const r2 = (x: number) => Math.round(x * 100) / 100;
const area = (p: Pt[]) => p.reduce((s, [x, y], i) => { const [x2, y2] = p[(i + 1) % p.length]; return s + x * y2 - x2 * y; }, 0) / 2;

/** Найближча точка на контурах і зовнішня нормаль (контури проти годинникової — зовнішні). */
function snap(polys: Pt[][], x: number, y: number) {
  let best = { d: Infinity, x, y, nx: 1, ny: 0 };
  for (const p of polys) for (let i = 0; i < p.length; i++) {
    const [ax, ay] = p[i], [bx, by] = p[(i + 1) % p.length];
    const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy;
    if (!len2) continue;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / len2));
    const px = ax + t * dx, py = ay + t * dy, d = (px - x) ** 2 + (py - y) ** 2;
    if (d < best.d) { const l = Math.sqrt(len2); best = { d, x: px, y: py, nx: dy / l, ny: -dx / l }; }
  }
  return best;
}

/** Чи точка всередині області (парність перетинів: дірки в контурах враховуються самі). */
function inside(polys: Pt[][], x: number, y: number) {
  let n = false;
  for (const p of polys) for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [xi, yi] = p[i], [xj, yj] = p[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) n = !n;
  }
  return n;
}

/** Точка для отвору в задній стінці: всередині області, де отвір не зачепить стінки; інакше — найближча на її краю. */
function snapBack(room: Pt[][], x: number, y: number) {
  if (!room.length) return null;
  if (inside(room, x, y)) return { x, y };
  const q = snap(room, x, y);
  return { x: q.x, y: q.y };
}

/** Дані останньої збірки: для міток, перетягування і файлів. ox, oy — зсув гліфа в сцені (залежить від розкладки). */
type Letter = { ch: string; x0: number; ox: number; oy: number; cx: number; cy: number; outer: Pt[][]; room: Pt[][]; contours: Pt[][]; body: THREE.BufferGeometry; lid: THREE.BufferGeometry };
type Built = { letters: Letter[]; p: S; zc: number; holes: (Hole & { x: number; y: number; nx: number; ny: number })[]; minX: number; maxX: number; minY: number; maxY: number; warn: string };

export function mount(root: HTMLElement, ctx: Ctx & { store?: Store }) {
  const t = T[ctx.lang] ?? T.uk;
  const s: S = {
    text: t.def, font: 'rubik-700', H: 100, D: 20,
    W: 2, B: 1.2, L: 0.8, SW: 0.8, SD: 4, C: 0.15,
    HW: 5, mount: 'none', HS: 4, light: COLORS[0], layout: 'h',
  };
  let holes: Hole[] = [];
  let view: View = 'assembled';

  // збережене в цій вкладці: перевіряємо кожне поле, бо там може бути що завгодно
  const restore = (raw: unknown) => {
    if (!raw || typeof raw !== 'object') return;
    const o = raw as { s?: Record<string, unknown>; holes?: unknown };
    const src = o.s ?? {};
    if (typeof src.text === 'string') s.text = src.text.slice(0, 24);
    if (FONTS.some(([id]) => id === src.font)) s.font = src.font as string;
    for (const k of Object.keys(LIM) as NumKey[]) { const v = src[k]; if (typeof v === 'number' && isFinite(v)) s[k] = clamp(v, LIM[k]); }
    if (src.mount === 'none' || src.mount === 'screws') s.mount = src.mount;
    if (src.layout === 'h' || src.layout === 'v') s.layout = src.layout;
    if (typeof src.light === 'string' && /^#[0-9a-f]{6}$/i.test(src.light)) s.light = src.light.toUpperCase();
    if (Array.isArray(o.holes)) holes = (o.holes as Hole[])
      .filter((h) => h && Number.isInteger(h.li) && h.li >= 0 && isFinite(h.u) && isFinite(h.v)).slice(0, 200)
      .map((h) => (h.back ? { li: h.li, u: h.u, v: h.v, back: true } : { li: h.li, u: h.u, v: h.v }));
  };
  try { restore(ctx.store?.load()); } catch { /* зіпсоване сховище — беремо типові */ }
  const persist = () => { try { ctx.store?.save({ s, holes }); } catch { /* сховище недоступне */ } };

  const stage = root.querySelector<HTMLElement>('[data-stage]')!;
  const panel = root.querySelector<HTMLElement>('[data-params]')!;
  const seg = (k: string, opts: [string, string][], cur: string, wide = true) =>
    `<div class="seg${wide ? ' wide' : ''}" data-seg="${k}">${opts.map(([v, l]) => `<button type="button" data-v="${v}" class="${v === cur ? 'on' : ''}">${l}</button>`).join('')}</div>`;
  stage.insertAdjacentHTML('afterbegin',
    `<div class="stage-top">${seg('view', (Object.keys(t.views) as View[]).map((v) => [v, t.views[v]]), view, false)}` +
    `${seg('layout', (Object.keys(t.layouts) as Layout[]).map((v) => [v, t.layouts[v]]), s.layout, false)}</div>`);
  const viewer = createStage(stage, ctx.lang).viewer;
  const layoutSeg = stage.querySelector<HTMLElement>('[data-seg="layout"]')!;

  // ---------- панель ----------
  const unit = `<i>${t.mm}</i>`;
  const rng = (k: NumKey, label: string) => {
    const [min, max, step] = LIM[k];
    return `<div class="rng" data-k="${k}"><span class="lbl">${label}</span><span class="rng-row">` +
      `<input type="range" min="${min}" max="${max}" step="${step}" value="${s[k]}" aria-label="${label}">` +
      `<span class="num"><input type="number" min="${min}" max="${max}" step="${step}" value="${s[k]}" inputmode="decimal" aria-label="${label}">${unit}</span></span>` +
      `<span class="minmax">${t.min} ${min} · ${t.max} ${max}</span></div>`;
  };

  panel.innerHTML =
    `<details class="group" open><summary>${t.text}</summary>` +
      `<label class="field flat"><span class="lbl">${t.text}</span><input type="text" data-text value="${esc(s.text)}" maxlength="24"></label>` +
      `<label class="field flat"><span class="lbl">${t.font}</span><select data-font>${FONTS.map(([id, n]) => `<option value="${id}"${id === s.font ? ' selected' : ''}>${n}</option>`).join('')}</select></label>` +
      rng('H', t.H) + rng('D', t.D) + `<p class="hint">${t.hintDepth}</p></details>` +
    `<details class="group" open><summary>${t.wire}</summary>` +
      rng('HW', t.HW) +
      `<p class="hint"><b data-holes></b> ${t.hintHoles}</p>` +
      `<div class="dl-more"><button type="button" class="btn" data-clear>${t.clear}</button></div></details>` +
    `<details class="group" open><summary>${t.light}</summary>` +
      `<div class="swatches" data-colors>${COLORS.map((c) => `<button type="button" data-c="${c}" style="--c:${c}" aria-label="${c}"></button>`).join('')}` +
      `<label class="swatch-pick" title="${t.light}"><input type="color" data-color value="${s.light}" aria-label="${t.light}"></label></div>` +
      `<p class="hint">${t.lightHint}</p></details>` +
    `<details class="group"><summary>${t.fit}</summary>` +
      rng('W', t.W) + rng('B', t.B) + rng('L', t.L) + rng('SW', t.SW) + rng('SD', t.SD) + rng('C', t.C) +
      `<p class="hint">${t.hintFit}</p></details>` +
    `<details class="group"><summary>${t.mount}</summary>` +
      seg('mount', [['none', t.none], ['screws', t.screws]], s.mount) +
      `<div data-screw-opts>${rng('HS', t.HS)}<p class="hint">${t.hintMount}</p></div></details>`;

  const $ = <E extends Element = HTMLElement>(q: string, el: ParentNode = panel) => el.querySelector<E>(q)!;

  panel.querySelectorAll<HTMLElement>('.rng').forEach((el) => {
    const k = el.dataset.k as NumKey;
    const [r, n] = el.querySelectorAll('input');
    const set = (raw: string) => {
      // пустое или не число — прежнее значение (раньше сбрасывалось на минимум)
      const x = raw.trim() === '' ? NaN : +raw;
      const v = Number.isFinite(x) ? clamp(x, LIM[k]) : s[k];
      s[k] = v; r.value = n.value = String(v);
      sync(); rebuild();
    };
    r.oninput = () => set(r.value);
    n.onchange = () => set(n.value);
  });
  $<HTMLInputElement>('[data-text]').oninput = (e) => { s.text = (e.target as HTMLInputElement).value; rebuild(); };
  $<HTMLSelectElement>('[data-font]').onchange = (e) => { s.font = (e.target as HTMLSelectElement).value; rebuild(); };
  root.querySelectorAll<HTMLElement>('[data-seg]').forEach((el) => {
    el.onclick = (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-v]');
      if (!b) return;
      el.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
      const k = el.dataset.seg!, v = b.dataset.v!;
      if (k === 'view') { setView(v as View); return; }
      if (k === 'layout') { s.layout = v as Layout; front = true; } else (s as any)[k] = v;
      sync(); rebuild();
      if (k === 'mount' && v === 'screws') reveal($('[data-screw-opts]'));
    };
  });
  /** Прокрутити до блоку, що щойно відкрився, — щоб не ховався під кнопками завантаження. */
  const reveal = (el: HTMLElement) => {
    const bar = document.querySelector<HTMLElement>('.actions');
    el.style.scrollMarginBottom = `${(bar?.offsetHeight ?? 0) + 12}px`;
    el.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  let bed = bedPicker(panel, ctx.lang, (b) => { bed = b; viewer.setBed(b.x, b.y); rebuild(); });
  viewer.setBed(bed.x, bed.y);
  const setLight = (c: string) => {
    s.light = c;
    $<HTMLInputElement>('[data-color]').value = c;
    panel.querySelectorAll<HTMLElement>('[data-c]').forEach((b) => b.classList.toggle('on', b.dataset.c === c));
    show(); persist();
  };
  $('[data-colors]').onclick = (e) => { const b = (e.target as HTMLElement).closest<HTMLElement>('[data-c]'); if (b) setLight(b.dataset.c!); };
  $<HTMLInputElement>('[data-color]').oninput = (e) => setLight((e.target as HTMLInputElement).value.toUpperCase());
  $('[data-clear]').onclick = () => { holes = []; rebuild(); };

  function sync() {
    $('[data-screw-opts]').hidden = s.mount !== 'screws';
    $('[data-holes]').textContent = t.holes(holes.length);
    $<HTMLButtonElement>('[data-clear]').disabled = !holes.length;
    layoutSeg.style.display = view === 'wall' ? '' : 'none';
  }

  // ---------- камера ----------
  // Сама ставиться лише при першому показі. «На стіні» дивиться спереду, а при виході повертає камеру, як була.
  let first = true, front = false;
  let saved: { pos: THREE.Vector3; tgt: THREE.Vector3 } | null = null;
  function setView(v: View) {
    if (v === view) return;
    if (v === 'wall') { saved = { pos: viewer.camera.position.clone(), tgt: viewer.controls.target.clone() }; front = true; }
    view = v;
    sync(); show();
    if (v !== 'wall' && saved) {
      viewer.camera.position.copy(saved.pos); viewer.controls.target.copy(saved.tgt); viewer.controls.update();
      saved = null;
    }
  }

  // ---------- геометрія ----------
  const dims = $('[data-dims]', stage), fit = $('[data-fit]', stage);
  let last: Built | null = null;
  const setReady = (on: boolean) => root.querySelectorAll<HTMLButtonElement>('[data-download]').forEach((b) => { b.disabled = !on; });
  let seq = 0, timer = 0;

  function rebuild() {
    clearTimeout(timer);
    sync();
    timer = window.setTimeout(() => {
      const my = ++seq;
      fit.className = 'fit'; fit.textContent = t.loading;
      build({ ...s }, holes.map((h) => ({ ...h }))).then((b) => {
        if (my !== seq) { for (const l of b.letters) { l.body.dispose(); l.lid.dispose(); } return; }
        if (last) for (const l of last.letters) { l.body.dispose(); l.lid.dispose(); }
        last = b;
        holes = b.holes.map(({ li, u, v, back }) => (back ? { li, u, v, back } : { li, u, v })); // точки вже притягнуті
        sync(); persist();
        fit.className = b.warn ? 'fit bad' : 'fit';
        fit.textContent = b.letters.length ? b.warn || t.ok : b.warn || t.empty;
        // без букв скачивать нечего (выходил пустой файл)
        setReady(b.letters.length > 0);
        const w = b.maxX - b.minX, h = b.maxY - b.minY;
        dims.innerHTML = b.letters.length
          ? `<b>${Math.round(w)}</b> × <b>${Math.round(h)}</b> × <b>${r2(b.p.D + b.p.L)}</b> ${t.mm} · ${t.letters(b.letters.length)}`
          : '';
        show();
      }, (e) => {
        console.error(e);
        if (my === seq) { fit.className = 'fit bad'; fit.textContent = t.fontErr; }
      });
    }, 120);
  }

  async function build(p: S, want: Hole[]): Promise<Built> {
    const [M, font] = await Promise.all([solid(), loadFont(`/a/fonts/${p.font}.ttf`)]);
    const { CrossSection: CS, Manifold: MF } = M;
    const k = scope();
    try {
      const miss = new Set<string>();
      const glyphs = glyphShapes(font, inFont(font, p.text, miss).trim(), p.H);
      // зсув кожного гліфа: рядком — увесь напис по центру; стовпчиком — кожна літера по центру, одна під одною
      let ofs: [number, number][];
      if (p.layout === 'v') {
        const pitch = Math.max(0, ...glyphs.map((g) => g.maxY - g.minY)) + p.H * 0.2;
        ofs = glyphs.map((g, i) => [-(g.minX + g.maxX) / 2, ((glyphs.length - 1) / 2 - i) * pitch - (g.minY + g.maxY) / 2]);
      } else {
        let a = Infinity, b = -Infinity, c = Infinity, d = -Infinity;
        for (const g of glyphs) { a = Math.min(a, g.minX); b = Math.max(b, g.maxX); c = Math.min(c, g.minY); d = Math.max(d, g.maxY); }
        ofs = glyphs.map(() => [-(a + b) / 2, -(c + d) / 2]);
      }
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      const SD = Math.min(p.SD, p.D - p.B - 1);
      const r = p.HW / 2;
      // отвір у бічній стінці — біля задньої, щоб провід ішов по стіні і не світився
      const zc = Math.min(p.B + r + 1, p.D - r - 0.5);
      const letters: Letter[] = [];
      const placed: Built['holes'] = [];
      const wide: string[] = [], noMount: string[] = [];
      const segs = 24;

      glyphs.forEach((g, gi) => {
        const [ox, oy] = ofs[gi];
        const G = k(k(new CS(g.contours, 'NonZero')).translate([ox, oy]));
        if (G.isEmpty()) return;
        const li = letters.length;
        const contours = G.toPolygons() as Pt[][];
        const outer = contours.filter((c) => area(c) > 0);
        const bb = G.bounds();
        minX = Math.min(minX, bb.min[0]); maxX = Math.max(maxX, bb.max[0]); minY = Math.min(minY, bb.min[1]); maxY = Math.max(maxY, bb.max[1]);
        const cx = (bb.min[0] + bb.max[0]) / 2, cy = (bb.min[1] + bb.max[1]) / 2;
        if (!fitsBed(bed, bb.max[0] - bb.min[0], bb.max[1] - bb.min[1], p.D)) wide.push(g.ch);

        const inner = k(G.offset(-p.W, 'Round', 2, segs));
        let body: Manifold = k(k(G.extrude(p.D)).subtract(k(k(inner.extrude(p.D)).translate([0, 0, p.B]))));
        const roomCS = k(inner.offset(-(r + 0.5), 'Round', 2, segs));
        const room = roomCS.isEmpty() ? [] : (roomCS.toPolygons() as Pt[][]);

        const ring = k(k(inner.offset(-p.C, 'Round', 2, segs)).subtract(k(inner.offset(-p.C - p.SW, 'Round', 2, segs))));
        let lid: Manifold = k(k(G.extrude(p.L)).translate([0, 0, p.D]));
        if (!ring.isEmpty() && SD > 0) lid = k(lid.add(k(k(ring.extrude(SD)).translate([0, 0, p.D - SD]))));

        // отвори під провід цієї літери
        const cuts: Manifold[] = [];
        const rel = (x: number, y: number) => ({ u: (x - ox - g.x) / p.H, v: (y - oy) / p.H });
        for (const h of want) {
          if (h.li !== li) continue;
          const x = ox + g.x + h.u * p.H, y = oy + h.v * p.H;
          if (h.back) {
            const q = snapBack(room, x, y);
            if (!q) continue; // літера затонка для отвору такого діаметра
            placed.push({ li, ...rel(q.x, q.y), back: true, x: q.x, y: q.y, nx: 0, ny: 0 });
            cuts.push(k(k(MF.cylinder(p.B + 2, r, r, 32)).translate([q.x, q.y, -1])));
            continue;
          }
          if (!outer.length) continue;
          const q = snap(outer, x, y);
          placed.push({ li, ...rel(q.x, q.y), x: q.x, y: q.y, nx: q.nx, ny: q.ny });
          const len = p.W + 2.4;
          cuts.push(k(k(k(k(MF.cylinder(len, r, r, 32)).rotate([0, 90, 0])).translate([-(p.W + 1.2), 0, 0]))
            .rotate([0, 0, (Math.atan2(q.ny, q.nx) * 180) / Math.PI])).translate([q.x, q.y, zc]));
        }
        // кріплення: верхня і нижня точки найбільшої області, де вміщується головка шурупа
        if (p.mount === 'screws') {
          const sroom = k(inner.offset(-(p.HS + 1), 'Round', 2, segs));
          const polys = sroom.isEmpty() ? [] : (sroom.toPolygons() as Pt[][]).sort((a, b) => Math.abs(area(b)) - Math.abs(area(a)));
          if (!polys.length) noMount.push(g.ch);
          else {
            const pts = polys[0];
            const top = pts.reduce((a, b) => (b[1] > a[1] ? b : a)), bot = pts.reduce((a, b) => (b[1] < a[1] ? b : a));
            for (const [x, y] of Math.hypot(top[0] - bot[0], top[1] - bot[1]) > p.HS * 4 ? [top, bot] : [top])
              cuts.push(k(k(MF.cylinder(p.B + 2, p.HS / 2, p.HS / 2, 32)).translate([x, y, -1])));
          }
        }
        if (cuts.length) {
          const all = k(MF.union(cuts));
          body = k(body.subtract(all));
          lid = k(lid.subtract(all));
        }
        letters.push({ ch: g.ch, x0: g.x, ox, oy, cx, cy, outer, room, contours, body: toGeometry(body), lid: toGeometry(lid) });
      });
      if (!letters.length) minX = maxX = minY = maxY = 0;
      const warn = [wide.length ? t.bedBad(wide, bedText(bed)) : noMount.length ? t.mountBad(noMount.join(' ')) : '', miss.size ? t.noGlyph.replace('{c}', [...miss].join(' ')) : ''].filter(Boolean).join(' · ');
      return { letters, p, zc, holes: placed, minX, maxX, minY, maxY, warn };
    } finally {
      k.free();
    }
  }

  // ---------- перегляд ----------
  const markers = new THREE.Group();
  const wall = new THREE.Group();
  viewer.world.add(markers, wall);
  const markerMat = new THREE.MeshStandardMaterial({ color: '#3B82F6', emissive: '#1D4ED8', emissiveIntensity: 0.4 });
  const hitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
  const UP = new THREE.Vector3(0, 1, 0), Z = new THREE.Vector3(0, 0, 1);
  let markerGeo: THREE.SphereGeometry | null = null;
  let plugGeo: THREE.CylinderGeometry | null = null;

  function show() {
    const b = last;
    if (!b) return;
    const night = view === 'wall';
    const lift = view === 'exploded' ? b.p.D * 0.6 + 15 : 0;
    viewer.setUpright(night);
    viewer.setGrid(!night);
    viewer.setBackground(night ? '#0B0C10' : null);
    viewer.setLight(night ? 0.22 : 1);
    viewer.setParts(b.letters.flatMap((l) => [
      { geometry: l.body, role: 'base' as const, look: night ? { color: '#2E3036', edges: false } : undefined },
      { geometry: l.lid, role: 'detail' as const, offset: [0, 0, lift] as [number, number, number],
        look: night ? { color: s.light, emissive: s.light, glow: 0.9, edges: false } : undefined },
    ]));
    drawMarkers();
    drawWall(night);
    const w = Math.max(b.maxX - b.minX, 40), h = Math.max(b.maxY - b.minY, 40);
    if (night && (front || first)) frameWall(w + b.p.H, h + b.p.H, (b.minY + b.maxY) / 2);
    else if (first) viewer.frame(w, h, b.p.D);
    if (night) front = false;
    first = false;
  }

  // Вид спереди в свободную часть сцены: між кнопками зверху і плашками розмірів знизу.
  function frameWall(w: number, h: number, cy: number) {
    const cam = viewer.camera, cv = viewer.canvas.getBoundingClientRect();
    if (!cv.height) return viewer.frameFront(w, h);
    const top = Math.max(0, stage.querySelector('.stage-top')!.getBoundingClientRect().bottom - cv.top + 8);
    const dims = stage.querySelector('[data-dims]')!.getBoundingClientRect();
    const bot = dims.height ? Math.max(0, cv.bottom - dims.top + 8) : 0;
    const free = Math.max(cv.height - top - bot, cv.height * 0.4);
    const tan = Math.tan((cam.fov * Math.PI) / 360);
    const d = Math.max((h / 2 / tan) * (cv.height / free), w / 2 / (tan * cam.aspect)) * 1.1;
    const shift = ((top - bot) / 2) * ((2 * d * tan) / cv.height); // мм: центр вільної частини нижче центру кадру
    const y = cy + shift;
    cam.position.set(0, y, d);
    viewer.controls.target.set(0, y, 0);
    viewer.controls.update();
  }

  function drawMarkers() {
    markers.clear();
    const b = last;
    if (!b) return;
    markerGeo?.dispose(); plugGeo?.dispose();
    markerGeo = new THREE.SphereGeometry(Math.max(b.p.HW / 2 + 2, b.p.H * 0.05), 20, 14);
    plugGeo = new THREE.CylinderGeometry(b.p.HW / 2, b.p.HW / 2, 1, 32);
    for (const h of b.holes) {
      // невидима сфера — зона дотику; синій циліндр усередині — сам отвір, у розмір
      const m = new THREE.Mesh(markerGeo, hitMat);
      m.add(new THREE.Mesh(plugGeo, markerMat));
      placeMarker(m, h.x, h.y, h.nx, h.ny, !!h.back);
      markers.add(m);
    }
  }

  /** Сфера — на поверхні стінки, циліндр — крізь стінку вздовж осі отвору, трохи виступає з обох боків. */
  function placeMarker(m: THREE.Object3D, x: number, y: number, nx: number, ny: number, back: boolean) {
    const p = last!.p, plug = m.children[0];
    if (back) {
      m.position.set(x, y, p.B);
      plug.position.set(0, 0, -p.B / 2);
      plug.quaternion.setFromUnitVectors(UP, Z);
      plug.scale.set(1, p.B + 1, 1);
    } else {
      m.position.set(x + nx * 0.5, y + ny * 0.5, last!.zc);
      plug.position.set(-nx * (p.W / 2 + 0.5), -ny * (p.W / 2 + 0.5), 0);
      plug.quaternion.setFromUnitVectors(UP, new THREE.Vector3(nx, ny, 0));
      plug.scale.set(1, p.W + 1, 1);
    }
  }

  // Стіна вночі: темна площина і ореол — розмиті контури літер кольором світла (canvas, тінь — працює в усіх браузерах).
  let wallFor: Built | null = null, wallLight = '';
  function drawWall(on: boolean) {
    const b = last!;
    if (on ? wallFor === b && wallLight === s.light : !wallFor && !wall.children.length) return;
    wallFor = on ? b : null; wallLight = s.light;
    for (const o of wall.children as THREE.Mesh[]) { o.geometry.dispose(); const m = o.material as THREE.MeshBasicMaterial; m.map?.dispose(); m.dispose(); }
    wall.clear();
    if (!on || !b.letters.length) return;
    const w = b.maxX - b.minX, h = b.maxY - b.minY, H = b.p.H;
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(w + H * 6, h + H * 4), new THREE.MeshStandardMaterial({ color: '#2A2B30', roughness: 1 }));
    plane.position.set((b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2, -0.4);
    wall.add(plane);

    const m = H * 0.8; // поле навколо напису під розмиття
    const sc = Math.min(3, 2048 / (w + 2 * m), 2048 / (h + 2 * m)); // пікселів на мм
    const cv = document.createElement('canvas');
    cv.width = Math.ceil((w + 2 * m) * sc); cv.height = Math.ceil((h + 2 * m) * sc);
    const g = cv.getContext('2d')!;
    const far = cv.width + 100;
    g.shadowColor = s.light;
    g.shadowOffsetX = far;
    g.fillStyle = '#000';
    const path = new Path2D();
    for (const l of b.letters) for (const c of l.contours) {
      c.forEach(([x, y], i) => { const px = (x - b.minX + m) * sc - far, py = (b.maxY + m - y) * sc; i ? path.lineTo(px, py) : path.moveTo(px, py); });
      path.closePath();
    }
    for (const blur of [H * 0.35, H * 0.12]) { g.shadowBlur = blur * sc; g.fill(path, 'evenodd'); }
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    const halo = new THREE.Mesh(new THREE.PlaneGeometry(w + 2 * m, h + 2 * m),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false }));
    halo.position.set((b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2, -0.2);
    wall.add(halo);
  }

  // ---------- отвори мишею і пальцем ----------
  const canvas = viewer.canvas;
  const local = (e: PointerEvent) => {
    const ray = viewer.raycaster(e).ray.clone();
    viewer.world.updateMatrixWorld();
    return ray.applyMatrix4(viewer.world.matrixWorld.clone().invert());
  };
  let drag: { i: number; li: number; back: boolean } | null = null;
  let down: { x: number; y: number; t: number } | null = null;
  let lastTap: { x: number; y: number; t: number } | null = null;

  // capture на сцені: спрацьовує раніше за OrbitControls, тож обертання можна вимкнути до його початку
  stage.addEventListener('pointerdown', (e) => {
    if (e.target !== canvas || !last) return;
    down = { x: e.clientX, y: e.clientY, t: performance.now() };
    const hit = viewer.raycaster(e).intersectObjects(markers.children, false)[0];
    if (!hit) return;
    const i = markers.children.indexOf(hit.object);
    drag = { i, li: last.holes[i].li, back: !!last.holes[i].back };
    viewer.controls.enabled = false;
  }, true);

  window.addEventListener('pointermove', (e) => {
    if (!drag || !last) return;
    const z = drag.back ? last.p.B : last.zc;
    const q = local(e).intersectPlane(new THREE.Plane(new THREE.Vector3(0, 0, 1), -z), new THREE.Vector3());
    const L = last.letters[drag.li];
    if (!q || !L) return;
    if (drag.back) {
      const h = snapBack(L.room, q.x, q.y);
      if (!h) return;
      placeMarker(markers.children[drag.i], h.x, h.y, 0, 0, true);
      holes[drag.i] = { ...toHole(drag.li, h.x, h.y), back: true };
    } else {
      const h = snap(L.outer, q.x, q.y);
      placeMarker(markers.children[drag.i], h.x, h.y, h.nx, h.ny, false);
      holes[drag.i] = toHole(drag.li, h.x, h.y);
    }
  });
  window.addEventListener('pointerup', (e) => {
    if (drag) { drag = null; viewer.controls.enabled = true; down = null; rebuild(); return; }
    if (!down || e.target !== canvas) return;
    const now = performance.now();
    const tap = Math.hypot(e.clientX - down.x, e.clientY - down.y) < 8 && now - down.t < 350;
    down = null;
    if (!tap) { lastTap = null; return; }
    if (lastTap && now - lastTap.t < 400 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30) {
      lastTap = null;
      doubleTap(e);
    } else lastTap = { x: e.clientX, y: e.clientY, t: now };
  });

  /** Точка у сцені → отвір у частках висоти від початку літери (як у build()). */
  const toHole = (li: number, x: number, y: number): Hole => {
    const L = last!.letters[li];
    return { li, u: (x - L.ox - L.x0) / last!.p.H, v: (y - L.oy) / last!.p.H };
  };

  function doubleTap(e: PointerEvent) {
    const b = last;
    if (!b) return;
    const rc = viewer.raycaster(e);
    const mk = rc.intersectObjects(markers.children, false)[0];
    if (mk) { holes.splice(markers.children.indexOf(mk.object), 1); rebuild(); return; }
    const hit = rc.intersectObjects(viewer.meshes(), false)[0];
    if (!hit) return;
    const mi = viewer.meshes().indexOf(hit.object as THREE.Mesh);
    const li = Math.floor(mi / 2), L = b.letters[li];
    if (!L) return;
    const pt = viewer.world.worldToLocal(hit.point.clone());
    // дно корпусу зсередини або задня стінка ззовні — отвір у задній стінці; решта — у бічній
    const back = mi % 2 === 0 && !!hit.face && Math.abs(hit.face.normal.z) > 0.7 && pt.z < b.p.B + 0.01;
    if (back) {
      const q = snapBack(L.room, pt.x, pt.y);
      if (!q) return;
      holes.push({ ...toHole(li, q.x, q.y), back: true });
    } else {
      const q = snap(L.outer, pt.x, pt.y);
      holes.push(toHole(li, q.x, q.y));
    }
    rebuild();
  }

  sync();
  setLight(s.light);
  rebuild();

  // ---------- файли ----------
  /** STL — усі деталі разом: корпуси як у написі, кришки (лицем донизу) поруч — нижче рядка або праворуч від стовпчика. ZIP — кожна деталь окремо, по центру. */
  async function model(): Promise<Model & { single?: THREE.BufferGeometry }> {
    while (!last) await new Promise((r) => setTimeout(r, 50));
    const b = last;
    const w = b.maxX - b.minX, h = b.maxY - b.minY, top = b.p.D + b.p.L;
    const parts: Part[] = [];
    const layout: THREE.BufferGeometry[] = [];
    b.letters.forEach((l, i) => {
      const n = String(i + 1).padStart(2, '0') + (/[\p{L}\p{N}]/u.test(l.ch) ? '-' + l.ch : '');
      const body = l.body.clone().translate(-l.cx, -l.cy, 0);
      const lid = l.lid.clone().rotateX(Math.PI).translate(-l.cx, l.cy, top);
      parts.push({ name: `${n}-body`, role: 'base', geometry: body }, { name: `${n}-lid`, role: 'detail', geometry: lid });
      layout.push(l.body.clone(), b.p.layout === 'v' ? lid.clone().translate(l.cx + w + 10, l.cy, 0) : lid.clone().translate(l.cx, l.cy - h - 10, 0));
    });
    const single = layout.length ? mergeGeometries(layout) : undefined;
    return { name: 'light-letters', parts, single: single ?? undefined };
  }
  return { model };
}
